import React from "react";
import { Wrench, Hourglass, Sparkle } from "@phosphor-icons/react";

import CaseActionBanner from "@/components/case/CaseActionBanner";
import PendingReceiptCard from "@/components/case/PendingReceiptCard";
import TreatmentTab from "@/components/case/TreatmentTab";
import TierAllowanceLedger from "@/components/case/TierAllowanceLedger";

/**
 * WorkflowTab — the doctor's actions hub on the case detail page.
 *
 * iter83 restructure (replaces the iter81 "Care & Production" mash-up):
 * action surfaces ONLY. Reference content (full shipment timeline,
 * fabrication specs, materials traceability, concerns history) moved
 * to the new dedicated "Production" tab.
 *
 * The tab is status-aware — what renders depends on where the case is
 * in its lifecycle. The doctor's eye always sees "what to do now",
 * not a kitchen sink of every possible surface.
 *
 *   approved / in_production  → action banner + spare empty state
 *   shipped                   → action banner + PendingReceiptCard
 *   aligners_received / in_treatment / treatment_started / refinement /
 *   completed                 → action banner + TreatmentTab (the engine
 *                               that holds the fitment + advance + linked
 *                               cases + care-card + patient photos
 *                               action surfaces)
 *
 * Everything else (draft, planning stages) doesn't render this tab at
 * all — the tab visibility allowlist in CaseDetail.jsx handles that.
 */
export default function WorkflowTab({ caseData: c, onReload }) {
    const status = c?.status;
    const isFabricating = status === "approved" || status === "in_production";
    const isShipping = status === "shipped" || status === "delivered";
    const isActive = [
        "aligners_received", "in_treatment", "treatment_started",
        "refinement_requested", "completed",
    ].includes(status);

    return (
        <div className="space-y-5" data-testid="workflow-tab">
            {/* Action banner — the "what to do now" hero, computed
                deterministically from status + shipments. Returns null
                when there's nothing meaningful to say. */}
            <CaseActionBanner caseData={c} />

            {/* iter92 — Tier allowance ledger. Self-hides on child cases,
                cases without a tier, and cases where the allowance fetch
                fails. Shows refinement + retainer usage at a glance. */}
            <TierAllowanceLedger caseData={c} />

            {/* Shipping: prominent confirm-receipt card. Auto-hides if
                there's no pending shipment (e.g. status flipped but
                the row is already marked received). */}
            {(isShipping || isActive) && (
                <PendingReceiptCard caseId={c.id} onConfirmed={onReload} />
            )}

            {/* Active treatment + completed: the TreatmentTab engine
                (fitment, advance, linked cases, care card, patient
                photos). Stays where it was — we just moved its host
                tab here. */}
            {isActive && <TreatmentTab caseData={c} onReload={onReload} />}

            {/* Pre-shipping: spare empty state so the tab doesn't look
                broken. The banner above already says what's happening;
                this just gives the page some texture. */}
            {isFabricating && (
                <div
                    className="rounded-2xl border border-dashed border-border bg-secondary/30 p-8 text-center"
                    data-testid="workflow-fabricating-empty"
                >
                    <Hourglass size={28} weight="duotone" className="mx-auto opacity-60 text-primary mb-3" />
                    <div className="font-display text-base font-semibold">Nothing for you to do right now</div>
                    <p className="text-sm text-muted-foreground mt-1 max-w-md mx-auto">
                        Your lab is fabricating the aligners. We'll move this tab into action mode the moment a shipment is dispatched.
                    </p>
                    <p className="text-[11px] text-muted-foreground mt-3 italic inline-flex items-center gap-1">
                        <Sparkle size={11} weight="fill" className="text-primary" />
                        Want to see what's being built? Check the <strong>Production</strong> tab.
                    </p>
                </div>
            )}

            {/* Belt-and-suspenders fallback — if a future status falls
                outside our allowlist, we still render *something*
                rather than a blank tab. Doctor sees a generic
                placeholder + a hint to use other tabs. */}
            {!isActive && !isShipping && !isFabricating && (
                <div
                    className="rounded-2xl border border-dashed border-border bg-secondary/30 p-8 text-center"
                    data-testid="workflow-generic-empty"
                >
                    <Wrench size={28} weight="duotone" className="mx-auto opacity-60 text-muted-foreground mb-3" />
                    <div className="font-display text-base font-semibold">No active workflow yet</div>
                    <p className="text-sm text-muted-foreground mt-1">
                        Once your case progresses past planning, your day-to-day actions will live here.
                    </p>
                </div>
            )}
        </div>
    );
}
